import {
  DEMO_RELEASE,
  demoCatalog,
  demoChip,
  demoPeripheral,
  demoProvenance,
  demoRegister,
} from "./demo";
import type {
  CatalogItem,
  Chip,
  Digest,
  Page,
  Peripheral,
  ProvenanceItem,
  Register,
} from "./types";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

async function request<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Catalog request failed (${response.status})`);
  return response.json() as Promise<T>;
}

export async function resolveRelease(): Promise<Digest> {
  try {
    const row = await request<{ release_digest: Digest }>("/api/v1/channels/stable");
    return row.release_digest;
  } catch {
    return DEMO_RELEASE;
  }
}

export async function searchCatalog(
  release: string,
  query: string,
  cursor?: string,
): Promise<Page<CatalogItem>> {
  try {
    const params = new URLSearchParams({ q: query });
    if (cursor) params.set("cursor", cursor);
    return await request(`/api/v1/releases/${encodeURIComponent(release)}/search?${params}`);
  } catch {
    const needle = query.toLocaleLowerCase();
    return {
      release_id: DEMO_RELEASE,
      next_cursor: null,
      items: demoCatalog.filter((item) =>
        `${item.name} ${item.subtitle} ${item.description}`.toLocaleLowerCase().includes(needle),
      ),
    };
  }
}

function revisionQuery(revision?: string): string {
  return revision ? `?revision=${encodeURIComponent(revision)}` : "";
}

export async function getChip(release: string, entity: string, revision?: string): Promise<Chip> {
  try {
    return await request(
      `/api/v1/releases/${encodeURIComponent(release)}/chips/${encodeURIComponent(entity)}${revisionQuery(revision)}`,
    );
  } catch {
    return demoChip;
  }
}
export async function getPeripheral(
  release: string,
  entity: string,
  revision?: string,
): Promise<Peripheral> {
  try {
    return await request(
      `/api/v1/releases/${encodeURIComponent(release)}/peripherals/${encodeURIComponent(entity)}${revisionQuery(revision)}`,
    );
  } catch {
    return demoPeripheral;
  }
}
export async function getRegister(
  release: string,
  entity: string,
  revision?: string,
): Promise<Register> {
  try {
    return await request(
      `/api/v1/releases/${encodeURIComponent(release)}/registers/${encodeURIComponent(entity)}${revisionQuery(revision)}`,
    );
  } catch {
    return demoRegister;
  }
}
export async function getProvenance(
  release: string,
  entity: string,
  revision?: string,
): Promise<ProvenanceItem[]> {
  try {
    const params = new URLSearchParams({ entity });
    if (revision) params.set("revision", revision);
    const page = await request<{ items: ProvenanceItem[] }>(
      `/api/v1/releases/${encodeURIComponent(release)}/provenance?${params}`,
    );
    return page.items;
  } catch {
    return demoProvenance;
  }
}

export function artifactUrl(release: string, format: string, filename: string): string {
  return `${API_BASE}/api/v1/releases/${encodeURIComponent(release)}/artifacts/${encodeURIComponent(format)}/${encodeURIComponent(filename)}`;
}
